import {defineStore} from 'pinia'
import router from '@/router'
import {api} from '@/services/default'

const BACK_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:8000"

export const useAuthStore = defineStore('auth', {
  state: () => ({
    access: sessionStorage.getItem('charterplace.access') || '',
    refresh: sessionStorage.getItem('charterplace.refresh') || '',
    user: JSON.parse(sessionStorage.getItem('charterplace.user') || 'null') || null,

    refreshInFlight: false,
    waiters: [],
    hasVerifiedOnce: false,
  }),
  getters: {
    isAuthenticated: (s) => !!s.access
  },
  actions: {
    setTokens(accessToken, refreshToken, user) {
      this.access = accessToken
      this.refresh = refreshToken
      this.user = user
      sessionStorage.setItem('charterplace.access', accessToken)
      sessionStorage.setItem('charterplace.refresh', refreshToken)
      sessionStorage.setItem('charterplace.user', JSON.stringify(user || null))
    },
    clearTokens() {
      this.access = ''
      this.refresh = ''
      this.user = null
      sessionStorage.removeItem('charterplace.access')
      sessionStorage.removeItem('charterplace.refresh')
      sessionStorage.removeItem('charterplace.user')
    },
    async login(loginData) {
      const url = BACK_URL + '/users/login/'
      const {data} = await api.post(url, loginData)
      this.setTokens(data.access, data.refresh, data.user)
    },
    async logout() {
      this.clearTokens()
    },
    async logoutAndRedirect(reason = '') {
      await this.logout()
      const q = {}
      if (reason) q.reason = reason
      if (router.currentRoute.value.name !== 'Login') {
        await router.push({name: 'Login', query: q})
      }
    },

    async refreshToken() {
      if (!this.refresh) {
        await this.logout()
        return Promise.reject(new Error('No refresh token'))
      }

      if (this.refreshInFlight) {
        return new Promise((resolve, reject) => {
          this.waiters.push({resolve, reject})
        })
      }

      this.refreshInFlight = true
      try {
        const url = BACK_URL + '/users/refresh/'
        const {data} = await api.post(url, {refresh: this.refresh})
        this.setTokens(data.access, this.refresh, this.user)
        this.waiters.forEach(w => w.resolve())
        this.waiters = []
        return true
      } catch (e) {
        this.waiters.forEach(w => w.reject(e))
        this.waiters = []
        await this.logout()
        throw e
      } finally {
        this.refreshInFlight = false
      }
    },

    async verifyWithTimeout(ms = 4000) {
      if (!this.access) {
        await this.logout()
        return false
      }
      const url = BACK_URL + '/users/verify/'
      const verifier = api.post(url, {token: this.access})
      const timeout = new Promise((_, rej) => {
        setTimeout(() => rej(new Error('verify-timeout')), ms)
      })
      try {
        await Promise.race([verifier, timeout])
        this.hasVerifiedOnce = true
        return true
      } catch (_) {
        await this.logout()
        return false
      }
    },

    async initAuth() {
      if (!this.isAuthenticated || this.hasVerifiedOnce) return
      await this.verifyWithTimeout(3000)
    },
  }
})
